import { Spinner } from "@fluentui/react-components";
import {
  ArrowUpRightRegular,
  CheckmarkRegular,
  LinkRegular,
} from "@fluentui/react-icons";
import { usePatchDiagnosticStatus } from "apps/project-iq/hooks/usePatchDiagnosticStatus";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { IDiagnosticRunProps } from "./DiagnosticRun.types";
import { useDiagnosticRunStyles } from "./DiagnosticRun.styles";

const RUN_CONFIG = {
  workshops_focus_groups: {
    subtitle:
      "Plan the workshop with AIMS — agenda, key questions and breakouts.",
    stepTitle: "Plan the session in AIMS",
    stepDescription: "Use the AIMS workshop prompt.",
  },
  in_person_interviews: {
    subtitle:
      "Prepare the interview guide with AIMS — tailored to your engagement.",
    stepTitle: "Prepare the guide in AIMS",
    stepDescription: "Use the AIMS interview prompt.",
  },
  survey: {
    subtitle:
      "Send the survey link to your audience and monitor responses inside SurveyMonkey.",
    stepTitle: "Distribute and collect responses",
    stepDescription: "Use SurveyMonkey to distribute and monitor responses.",
  },
} as const;

export const DiagnosticRun = ({
  diagnosticId,
  method,
  isConfigured,
}: IDiagnosticRunProps): JSX.Element => {
  const styles = useDiagnosticRunStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const { mutate, isLoading, isError } = usePatchDiagnosticStatus(diagnosticId);
  const { links: runLinks, isLoading: isLinksLoading } = useLinks(
    "nh_diagnostic_iq_diagnostic_run_links",
  );
  const config = RUN_CONFIG[method];
  const runLink = runLinks.find((link) => link.linkType === method);
  const configUrl = runLink?.url;
  const buttonLabel = runLink?.displayName;
  const runSubtext = runLink?.subtext;
  const handleMarkConfigured = () => mutate({ status: "in_progress" });
  const handleReset = () => mutate({ status: "draft" });

  return (
    <div className={styles.card}>
      <div className={styles.headerRow}>
        <h2 className={styles.heading}>Run</h2>
        {isConfigured && (
          <span
            className={styles.configuredBadge}
            aria-label="Status: DATA COLLECTED"
          >
            ✓ DATA COLLECTED
          </span>
        )}
      </div>
      <p className={styles.subtitle}>{config.subtitle}</p>
      <div className={styles.stepList}>
        <div className={styles.stepRow}>
          <div className={styles.stepBody}>
            <p className={styles.stepTitle}>{config.stepTitle}</p>
            <p className={styles.stepDesc}>{config.stepDescription}</p>
          </div>
          {isLinksLoading ? (
            <button
              type="button"
              className={styles.openLink}
              disabled
              aria-label="Loading link…"
              aria-busy="true"
            >
              <Spinner size="extra-tiny" />
            </button>
          ) : configUrl && buttonLabel ? (
            <a
              href={configUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${buttonLabel} (opens in new tab)`}
              className={styles.openLinkCard}
            >
              <div className={styles.openLinkCardIcon} aria-hidden="true">
                <LinkRegular />
              </div>
              <div className={styles.openLinkCardContent}>
                <div className={styles.openLinkCardTitle}>{buttonLabel}</div>
                {runSubtext && (
                  <div className={styles.openLinkCardSubtext}>{runSubtext}</div>
                )}
              </div>
              <div className={styles.openLinkCardArrow} aria-hidden="true">
                <ArrowUpRightRegular />
              </div>
            </a>
          ) : null}
        </div>
      </div>
      {isError && (
        <p className={styles.errorText} role="alert">
          Something went wrong. Please try again.
        </p>
      )}
      <div className={styles.footer}>
        {isConfigured ? (
          <>
            <span className={styles.footerText}>
              All data collected — ready for analysis.
            </span>
            <button
              type="button"
              className={sharedButtonStyles.secondaryButton}
              onClick={handleReset}
              disabled={isLoading}
              aria-label={isLoading ? "Resetting…" : "Reset configuration"}
              aria-busy={isLoading}
            >
              Reset
            </button>
          </>
        ) : (
          <>
            <span className={styles.footerText}>
              Mark complete after the session wraps.
            </span>
            <button
              type="button"
              className={styles.markConfiguredButton}
              onClick={handleMarkConfigured}
              disabled={isLoading}
              aria-label={isLoading ? "Saving…" : "Mark data collected"}
              aria-busy={isLoading}
            >
              <CheckmarkRegular aria-hidden="true" />
              Mark data collected
            </button>
          </>
        )}
      </div>
    </div>
  );
};
